import { useState, useEffect, useCallback, useMemo } from "react";
import FormDetails from "components/FormDesigner/FormDetails";
import ElementList from "components/FormDesigner/ElementList";
import useElementList from "hooks/FormDesigner/useElementList";
import { useLocation, Prompt, Redirect, useHistory } from "react-router-dom";
import { Button, Elem, ElemBefore, Loading } from "@alm/flow-ds/components";
import { useTranslation, Trans } from "react-i18next";
import PageHeader from "components/PageHeader";
import { status } from "../../../constants";
import exportForm, { FormJSON, convertForm } from "exportForm/exportForm";
import api from "lib/api";
import BusinessLogicList from "../Properties/BusinessLogicList";
import SaveDialog from "../SaveDialog";
import Breadcrumb from "components/Breadcrumb";
import axios from "axios";
import { useAlertsHandler } from "hooks/useAlertsHandler";
import { setFormFullWidth } from "data/app/appActions";
import { useDispatch } from "react-redux";
import Performance from "../Performance";

export default function FormDesigner() {
	const { t } = useTranslation();

	const location = useLocation();
	const history = useHistory();

	const search = location?.search;
	const queryParams = useMemo(() => new URLSearchParams(search), [search]);

	const { addAlert, addErrorAlert } = useAlertsHandler();

	// Scrolling element
	const formWindow = document.querySelector(`.content-wrapper`);

	// formId and formEntity are only used when creating a brand new form
	const formId: string = queryParams.get("formId") as string;
	const [formEntity, setFormEntity] = useState(
		queryParams.get("formEntity") !== "" &&
			queryParams.get("formEntity") !== null
			? (queryParams.get("formEntity") as string)
			: "NoEntity"
	);

	// Only used when a form is loaded into the designer
	const formDefinition: string = useMemo(
		() => queryParams.get("formDefinition") as string,
		[queryParams]
	);

	// Scroll actice or not
	const [isScrollActive, setIsScrollActive] = useState(false);

	const TENANT: string = useMemo(
		() =>
			window.location.hostname.substring(
				0,
				window.location.hostname.indexOf(".")
			) || "",
		[]
	);

	const {
		state,
		addElement,
		moveElement,
		removeElement,
		updateElement,
		initForm,
		updateFormLabel,
		updateEntityData,
		updateValidation,
		updateElementValidation,
		loadForm,
		updateFormBusinessLogic,
		updateFormBusinessLogicValidation,
		updateFormLayoutFullWidth,
		updateFormLayoutMaxWidth,
		updateAdditionalSelect,
		updateSelectFieldsBasedOnForm,
	} = useElementList();

	const [dirty, setDirty] = useState(false);
	const [formReady, setFormReady] = useState(false);
	const [entityDataReady, setEntityDataReady] = useState(false);

	const [downloadLoading, setDownloadLoading] = useState(false);

	// TODO: we need to make a generic backend concept that is available through perhaps context
	const [isEmu, setIsEmu] = useState(false);

	// Get entity data
	const getEntityData = useCallback(async () => {
		if (formEntity) {
			try {
				const data = await api.entity.getEntityData(formEntity);
				updateEntityData(data);
			} catch (error) {
				addAlert("errorLoadingFormList", status.ERROR);
				if (error && axios.isAxiosError(error))
					console.error(`Error: ${error.message}`);
			}
			setEntityDataReady(true);
		}
	}, [addAlert, formEntity, updateEntityData]);

	const reduxDispatch = useDispatch();

	// Conversion used when loading a form
	const runConversion = useCallback(async () => {
		const almostForm = location.state
			? (location.state as FormJSON)
			: await api.definitions.getFormDefinition(formDefinition);
		reduxDispatch(
			setFormFullWidth(almostForm?.view?.formLayout?.fullWidth || false)
		);
		const finalForm =
			almostForm !== undefined
				? convertForm(almostForm, addAlert, addErrorAlert)
				: undefined;
		if (finalForm !== undefined) {
			setFormEntity(finalForm.formEntity.label);
			const authType = await api.tenants.getBackendType(TENANT);
			let tempIsEmu = false;
			if (authType !== "collections") {
				setIsEmu(true);
				tempIsEmu = true;
			}
			loadForm({
				...finalForm,
				...(formDefinition ? { formId: formDefinition } : {}),
				formEntity: {
					...finalForm.formEntity,
				},
				usePatch: tempIsEmu,
			});
			setFormReady(true);
		} else {
			window.location.pathname = "/formdesigner";
			window.location.search = "";
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		TENANT,
		addAlert,
		addErrorAlert,
		formDefinition,
		loadForm,
		location.state,
	]);

	// Runs when creating a new form
	const handleNewForm = useCallback(async () => {
		const authType = await api.tenants.getBackendType(TENANT);

		let tempIsEmu = false;
		if (authType !== "collections") {
			setIsEmu(true);
			tempIsEmu = true;
		}
		initForm({
			formId,
			formEntity: {
				label: formEntity,
				value:
					formEntity === "NoEntity"
						? null
						: `cdp:/${TENANT}/resources/${formEntity}`,
			},
			usePatch: tempIsEmu,
		});
		setFormReady(true);
	}, [TENANT, formEntity, formId, initForm]);

	/**
	 * Handles redirecting after a save as action
	 *
	 * @param saveAs formDefinition to redirect to
	 */
	const saveAsCb = useCallback(
		saveAs => {
			if (formDefinition !== saveAs) {
				setDirty(false);
				setFormReady(false);
				history.push({
					pathname: "/formdesigner/create",
					search: `?formDefinition=${saveAs}`,
				});
			}
		},
		[formDefinition, history]
	);

	// Set entity data to be used throughout form
	useEffect(() => {
		if (formEntity) getEntityData();
	}, [formEntity, getEntityData, formDefinition]);

	// Depending on whether a form is loaded or it's a brand new form
	// it will either runConversion or handleNewForm
	useEffect(() => {
		if (formDefinition || location.state) {
			runConversion();
		} else if (formId && formEntity) {
			handleNewForm();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formDefinition]);

	// Check to see if form inputs have been edited
	useEffect(() => {
		if (!dirty && formReady) setDirty(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		state.elements,
		state.formLabel,
		state.onLoad,
		state.beforeSave,
		state.usePatch,
	]);

	useEffect(() => {
		if (process.env.NODE_ENV === "production")
			if (dirty) window.onbeforeunload = () => true;
			else window.onbeforeunload = null;
	}, [dirty]);

	useEffect(() => {
		// If scroll is bigger than the breadcrumb we set scroll is active
		formWindow?.addEventListener("scroll", () => {
			if (formWindow.scrollTop > 0) {
				setIsScrollActive(true);
			} else {
				setIsScrollActive(false);
			}
		});
	}, [formWindow]);

	const onDownloadForm = async () => {
		setDownloadLoading(true);
		await exportForm({
			elements: state.elements,
			formId: state.formId,
			formEntity: state.formEntity.value,
			formLabel: state.formLabel,
			formLayout: state.formLayout,
			selectFieldsBasedOnForm: state.selectFieldsBasedOnForm,
			additionalSelect: state.additionalSelect,
			updateValidation,
			addAlert,
			addErrorAlert,
			beforeSaveValidation: state.beforeSaveValidation,
			onLoadValidation: state.onLoadValidation,
			beforeSave: state.beforeSave,
			onLoad: state.onLoad,
			usePatch: state.usePatch,
		});
		setDownloadLoading(false);
	};

	const onSaveForm = async (saveAs: string | undefined) => {
		await exportForm({
			elements: state.elements,
			formId: saveAs !== undefined ? saveAs : state.formId,
			formEntity: state.formEntity.value,
			formLabel: state.formLabel,
			formLayout: state.formLayout,
			selectFieldsBasedOnForm: state.selectFieldsBasedOnForm,
			additionalSelect: state.additionalSelect,
			updateValidation,
			addAlert,
			addErrorAlert,
			isSave: true,
			saveAs,
			beforeSaveValidation: state.beforeSaveValidation,
			onLoadValidation: state.onLoadValidation,
			beforeSave: state.beforeSave,
			onLoad: state.onLoad,
			usePatch: state.usePatch,
			saveAsCb,
		});
		setDirty(false);
	};

	if (
		!(
			(formId && formEntity && !formDefinition) ||
			(formDefinition && !formId && formEntity !== "")
		)
	) {
		return <Redirect to="/formdesigner" />;
	}

	return (
		<div>
			<div
				className={`fixed-header-container fixed-header-container--form-designer ${
					isScrollActive ? "scroll__active" : ""
				}`}>
				<div className="container">
					<Breadcrumb
						crumb={[
							{
								name: t("formDesigner.text.forms"),
								link: "/formdesigner",
							},
							{ name: state.formId },
						]}
					/>
					<PageHeader title={t("formDesigner.title")}>
						<div className="page-header__actions">
							<Button
								className={`btn-tertiary download-btn${
									downloadLoading && " btn--loading"
								}`}
								label={
									!downloadLoading &&
									t("common.labels.download")
								}
								onClick={onDownloadForm}
								disabled={state.elements.length < 1}>
								{downloadLoading && <Loading />}
							</Button>
							<SaveDialog
								saveForm={onSaveForm}
								elementsLength={state.elements.length}
								currentFormId={
									formDefinition !== null ? undefined : formId
								}
							/>
						</div>
					</PageHeader>
				</div>
			</div>
			<div className="container">
				<Prompt when={dirty} message={t("formDesigner.text.unsaved")} />
				{formReady && entityDataReady ? (
					<>
						<Trans
							i18nKey={
								formId && formEntity
									? "formDesigner.text.currentlyCreating"
									: "formDesigner.text.currentlyEditing"
							}
							className="form-desc"
							parent="div">
							{/* @ts-expect-error Child type doesn't like React node */}
							<strong>{{ formId: state.formId }}</strong>
							<strong>
								{{
									/* @ts-expect-error Child type doesn't like React node */
									formEntity:
										state.formEntity.label === "NoEntity"
											? t("formDesigner.labels.noEntity")
											: state.formEntity.label,
								}}
							</strong>
						</Trans>
						<FormDetails
							formLabel={state.formLabel}
							formValidation={state.validation}
							formLayout={state.formLayout}
							updateFormLabel={updateFormLabel}
							updateFormLayoutFullWidth={
								updateFormLayoutFullWidth
							}
							updateFormLayoutMaxWidth={updateFormLayoutMaxWidth}
						/>
						<Elem className="elem-elements">
							<ElemBefore
								label={t("formDesigner.labels.elements")}
							/>
							<div className="hint-text">
								{t(`formDesigner.elements.common.elements`)}
							</div>
							<ElementList
								isEmu={isEmu}
								state={state}
								addAlert={addAlert}
								addElement={addElement}
								moveElement={moveElement}
								removeElement={removeElement}
								updateElement={updateElement}
								updateValidation={updateValidation}
								updateElementValidation={
									updateElementValidation
								}
							/>
						</Elem>
						<BusinessLogicList
							addAlert={addAlert}
							actions={["onLoad", "beforeSave"]}
							updateStore={updateFormBusinessLogic}
							elementIndex={0}
							value={{
								onLoad: state.onLoad,
								beforeSave: state.beforeSave,
							}}
							required={false}
							id={"formBusinessLogic"}
							validation={state.validation}
							updateFormBusinessLogicValidation={
								updateFormBusinessLogicValidation
							}
							desc={t(`businessLogic.formBusinessLogic`)}
						/>
						<Performance
							selectFieldsBasedOnForm={
								state.selectFieldsBasedOnForm
							}
							updateSelectFieldsBasedOnForm={
								updateSelectFieldsBasedOnForm
							}
							additionalSelect={state.additionalSelect}
							updateAdditionalSelect={updateAdditionalSelect}
							usePatch={state.usePatch}
							idList={state.formEntity.data}
							isEmu={isEmu}
						/>
					</>
				) : (
					<Loading />
				)}
			</div>
		</div>
	);
}
